import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import {
  CLARITY_ONE_PROJECT_STATUS_LABEL,
  CLARITY_ONE_PROJECT_TABS,
  type ClarityOneProjectTab,
} from "@shared/clarityOneProjects";
import { formatDate } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronDown, ChevronUp, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatSgd(amount: number): string {
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
  }).format(Number.isFinite(amount) ? amount : 0);
}

function Empty({ message }: { message: string }) {
  return <p className="text-sm text-[#8B7355] py-4">{message}</p>;
}

function statusPillClass(tab: ClarityOneProjectTab): string {
  switch (tab) {
    case "pending_approval":
      return "bg-[#C4A574]/15 text-[#8B7355] border border-[#C4A574]/40";
    case "active":
      return "bg-emerald-50 text-emerald-800 border border-emerald-200";
    case "completed":
      return "bg-sky-50 text-sky-900 border border-sky-200";
    case "cancelled":
      return "bg-[#F0EDE6] text-[#8B7355] border border-[#E8E4DC]";
  }
}

function parseTab(raw: string | null): ClarityOneProjectTab {
  const v = String(raw ?? "").trim();
  if ((CLARITY_ONE_PROJECT_TABS as readonly string[]).includes(v)) {
    return v as ClarityOneProjectTab;
  }
  return "active";
}

export default function ClarityOneProposalsPage() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const initialTab = parseTab(searchParams.get("tab"));
  const [tab, setTab] = useState<ClarityOneProjectTab>(initialTab);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const { data, isLoading } = trpc.clarityOneRecords.listProjects.useQuery(undefined, {
    enabled: !!me,
    refetchOnWindowFocus: false,
  });
  const getPdf = trpc.clarityOneRecords.getProposalPdfUrl.useMutation({
    onSuccess: (res) => {
      window.open(res.url, "_blank", "noopener,noreferrer");
    },
    onError: (e) => toast.error(e.message || "This PDF is unavailable right now."),
  });

  useEffect(() => {
    setTab(parseTab(searchParams.get("tab")));
  }, [searchParams]);

  // When landing without ?tab=, prefer Pending Approval if Active is empty.
  useEffect(() => {
    if (!data) return;
    if (searchParams.get("tab")) return;
    if ((data.counts.active ?? 0) === 0 && (data.counts.pending_approval ?? 0) > 0) {
      setTab("pending_approval");
    }
  }, [data, searchParams]);

  useEffect(() => {
    if (!meLoading && !me) setLocation(clarityOneLoginUrlWithRedirect("/proposals"));
  }, [meLoading, me, setLocation]);

  const selectTab = (next: ClarityOneProjectTab) => {
    setTab(next);
    const params = new URLSearchParams(search);
    if (next === "active") params.delete("tab");
    else params.set("tab", next);
    const q = params.toString();
    setLocation(q ? `/proposals?${q}` : "/proposals");
  };

  if (meLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" aria-label="Loading" />
        </div>
      </ClarityOneLayout>
    );
  }

  const counts = data?.counts ?? {
    pending_approval: 0,
    active: 0,
    completed: 0,
    cancelled: 0,
  };
  const projects = (data?.projects ?? []).filter((p) => p.tab === tab);

  return (
    <ClarityOneLayout showNav>
      <Link
        href="/home"
        className="mb-6 inline-flex items-center gap-1 text-sm text-[#8B7355] no-underline hover:text-[#1A2744]"
      >
        <ChevronLeft className="h-4 w-4" /> Home
      </Link>
      <h1 className="mb-3 text-3xl font-light tracking-tight text-[#1A2744]">My Projects</h1>
      <p className="mb-8 max-w-xl text-sm leading-relaxed text-[#8B7355]">
        Your Clarity projects, contract values, and project history — including Variation Orders.
      </p>

      <div
        className="mb-8 flex flex-wrap gap-2 border-b border-[#E8E4DC] pb-3"
        role="tablist"
        aria-label="Project status"
      >
        {CLARITY_ONE_PROJECT_TABS.map((id) => {
          const active = tab === id;
          const label = CLARITY_ONE_PROJECT_STATUS_LABEL[id];
          const count = counts[id] ?? 0;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => selectTab(id)}
              className={
                active
                  ? "rounded-full bg-[#1A2744] px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-white"
                  : "rounded-full border border-[#E8E4DC] bg-white px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-[#8B7355] hover:border-[#B8956E]"
              }
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-[#B8956E]" aria-label="Loading" />
      ) : projects.length === 0 ? (
        <Empty
          message={
            tab === "pending_approval"
              ? "Nothing waiting for your approval right now."
              : tab === "active"
                ? "No active projects yet."
                : tab === "completed"
                  ? "No completed projects yet."
                  : "No cancelled projects."
          }
        />
      ) : (
        <div className="space-y-4">
          {projects.map((p) => {
            const open = Boolean(expanded[p.proposalId]);
            return (
              <article
                key={p.proposalId}
                className="rounded-2xl border border-[#E8E4DC] bg-white px-5 py-5 sm:px-6"
                data-testid="clarity-one-project-card"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="min-w-0 flex-1 break-words text-lg font-medium leading-snug text-[#1A2744]">
                    {p.projectTitle}
                  </h2>
                  <span
                    className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wide ${statusPillClass(p.tab)}`}
                  >
                    {p.statusLabel}
                  </span>
                </div>

                <dl className="mt-4 grid grid-cols-1 gap-3 text-sm text-[#8B7355] sm:grid-cols-2">
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide">Proposal Reference</dt>
                    <dd className="font-mono text-[#1A2744]">{p.proposalRef}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide">Current Contract Value</dt>
                    <dd className="text-base font-medium text-[#1A2744]">
                      {formatSgd(p.currentContractValue)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide">Accepted Date</dt>
                    <dd className="text-[#1A2744]">
                      {p.acceptedDate ? formatDate(p.acceptedDate) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide">Consultant</dt>
                    <dd className="break-words text-[#1A2744]">{p.consultant || "—"}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2">
                  {p.canViewProposal ? (
                    <Button asChild size="sm" className="bg-[#1A2744] text-white hover:bg-[#1A2744]/90">
                      <Link href={`/proposals/${p.proposalId}`}>View Proposal</Link>
                    </Button>
                  ) : null}
                  {p.canDownloadPdf ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-[#E8E4DC]"
                      disabled={getPdf.isPending}
                      onClick={() => getPdf.mutate({ proposalId: p.proposalId })}
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                      Download PDF
                    </Button>
                  ) : null}
                  {p.pendingVoToken ? (
                    <Button asChild size="sm" variant="outline" className="border-[#E8E4DC]">
                      <Link href={`/vo/${p.pendingVoToken}`}>Review Variation Order</Link>
                    </Button>
                  ) : null}
                  {p.history.length > 0 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-[#8B7355]"
                      onClick={() =>
                        setExpanded((prev) => ({
                          ...prev,
                          [p.proposalId]: !prev[p.proposalId],
                        }))
                      }
                    >
                      Project History
                      {open ? (
                        <ChevronUp className="ml-1 h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="ml-1 h-3.5 w-3.5" />
                      )}
                    </Button>
                  ) : null}
                </div>

                {open ? (
                  <div className="mt-5 border-t border-[#E8E4DC] pt-4">
                    <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.15em] text-[#B8956E]">
                      Project History
                    </p>
                    <ol className="space-y-3">
                      {p.history.map((ev) => (
                        <li key={ev.id} className="text-sm">
                          <p className="text-[11px] uppercase tracking-wide text-[#8B7355]">
                            {ev.date ? formatDate(ev.date) : "—"}
                          </p>
                          <p className="font-medium text-[#1A2744]">{ev.title}</p>
                          {ev.detail ? (
                            <p className="text-[#8B7355]">{ev.detail}</p>
                          ) : null}
                          {ev.href ? (
                            <Link
                              href={ev.href}
                              className="mt-1 inline-block text-[12px] text-[#1A2744] underline-offset-2 hover:underline"
                            >
                              {ev.kind.startsWith("vo_")
                                ? "View Variation Order"
                                : "View Proposal"}
                            </Link>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </ClarityOneLayout>
  );
}
